import { useCallback, useEffect, useState } from "react";
import FormField from "./FormField";
import * as Yup from "yup";
import { sortFormFields } from "@/utility/form-utils";
import { useLanguageContext, useAuthContext } from "@/hooks/context-hooks";

const hasOtherOption = (options) => {
    if (!Array.isArray(options)) return false;
    return options.some(opt => {
        if (!opt) return false;
        if (typeof opt === "string") {
            return opt.toUpperCase() === "OTHER";
        }
        const valStr = opt.value !== undefined && opt.value !== null ? String(opt.value).toUpperCase() : "";
        const labelStr = opt.label !== undefined && opt.label !== null ? String(opt.label).toUpperCase() : "";
        return valStr === "-1" || valStr === "OTHER" || labelStr === "OTHER";
    });
};

export default function DynamicForm({
    children,
    metadata,
    schema,
    validations,
    onSave,
    isEditForm = false,
    fieldContainerClassName = "col-xl-6 col-lg-12 col-md-6",
}) {
    const { language } = useLanguageContext();
    const { user } = useAuthContext();
    const [formData, setFormData] = useState(schema);
    const [validationSchema, setValidationSchema] = useState({})
    const [errors, setErrors] = useState(null);
    const [optionsMap, setOptionsMap] = useState({});
    const [formFields, setFormFields] = useState([]);

    const translateValue = (value) => {
        return language ? (language(value) || value) : value;
    };

    useEffect(() => {
        setFormFields(sortFormFields(metadata?.fields));
        initValidationSchema();
    }, [metadata, validations]);

    useEffect(() => {
        if (isEditForm && schema) initializeEditFormData();
    }, [isEditForm, schema, metadata]);

    useEffect(() => {
        if (Object.keys(optionsMap).length > 0) {
            setFormData(prev => calculateFormFields({ ...prev }));
        }
    }, [optionsMap]);

    const initializeEditFormData = () => {
        setFormData(prevState => {
            if (metadata?.editFormStateFn) {
                const mutatedState = metadata.editFormStateFn(prevState) || {};
                return { ...prevState, ...schema, ...mutatedState };
            }
            return { ...prevState, ...schema };
        });
    }

    const initValidationSchema = () => {
        const preparedValidationSchema = prepareValidationSchema();
        setValidationSchema(Yup.object().shape({ ...preparedValidationSchema }));
    }

    const prepareValidationSchema = () => {
        const schemaFields = {};

        for (const field in validations) {
            if (validations.hasOwnProperty(field)) {
                let fieldValidation;

                // Extract type as the base of validation (e.g., "string", "number")
                const objType = validations[field].find(rule => rule === "array");
                const type = validations[field].find(rule => rule === "string" || rule === "number" || rule === "boolean");
                if (type) {
                    fieldValidation = Yup[type](`${field} must be a valid ${type} value`);
                } else {
                    console.warn(`Unsupported type for field "${field}"`);
                    continue;
                }

                if (objType) {
                    fieldValidation = Yup[objType]('${field} must be a valid ${type} value')?.of(fieldValidation);
                }

                // Process remaining validation rules
                validations[field].forEach(rule => {
                    if (rule === "required") {
                        fieldValidation = fieldValidation.required(`${field} is required`);
                    } else if (rule.startsWith("min:")) {
                        const min = parseInt(rule.split(":")[1], 10);
                        fieldValidation = fieldValidation.min(min, `${field} must be at least ${min}`);
                    } else if (rule.startsWith("max:")) {
                        const max = parseInt(rule.split(":")[1], 10);
                        fieldValidation = fieldValidation.max(max, `${field} must be at most ${max}`);
                    } else if (rule === "email") {
                        fieldValidation = fieldValidation.email(`${field} must be a valid email`);
                    }
                });

                schemaFields[field] = fieldValidation;
            }
        }

        return schemaFields;
    }

    /**
     * Evaluates the visibility condition for a field.
     * Supports two forms:
     *  - showCondition: (state) => boolean  — arbitrary function
     *  - showConditionIds: { field: "DW_D_Type", ids: ["1650", "1651"] } — array match
     * If neither is defined, the field is always visible.
     */
    const evaluateShowCondition = (field, state) => {
        if (typeof field.showCondition === "function") {
            return field.showCondition(state);
        }
        if (field.showConditionIds) {
            const { field: condField, ids } = field.showConditionIds;
            return Array.isArray(ids) && ids.includes(state?.[condField]);
        }
        return true;
    };

    const handleInputChange = (e, fieldName, fieldValue) => {

        const updatedFormData = { ...formData, [fieldName]: fieldValue };
        const calculatedFormData = calculateFormFields(updatedFormData);
        setFormData(calculatedFormData);

        // Find if this field has any dependent fields
        // Only process fields that:
        // 1. Have a non-empty value in the current form state
        // 2. Satisfy showCondition / showConditionIds based on updated state
        const eligibleFields = metadata.fields?.filter((field) => {
            const hasValue = calculatedFormData[field.name] !== undefined &&
                calculatedFormData[field.name] !== null &&
                calculatedFormData[field.name] !== "";
            const conditionMet = evaluateShowCondition(field, calculatedFormData);
            return hasValue && conditionMet;
        });

        eligibleFields?.forEach(async (field) => {
            if (field.dependsOn === fieldName) {
                let updatedOptions = [];

                if (typeof field.options === "function") {
                    // Prefer optionsKey (static group name) over the parent's selected value
                    const optionsFetchKey = field.optionsKey || fieldValue;
                    updatedOptions = await field.options(optionsFetchKey);
                }

                setOptionsMap((prevState) => ({
                    ...prevState,
                    [field.name]: updatedOptions,
                }));
            }
        });
    }

    const calculateFormFields = (updatedState) => {
        metadata.fields?.forEach(field => {
            if (field.type === "select" || field.type === "radio" || field.type === "pills") {
                const value = updatedState[field.name];
                if (value !== undefined && value !== null && value !== "") {
                    const options = optionsMap[field.name] || (Array.isArray(field.options) ? field.options : []);
                    const selectedOpt = options.find(opt => {
                        const optVal = typeof opt === "string" ? opt : opt?.value;
                        return String(optVal) === String(value);
                    });
                    if (selectedOpt) {
                        const rawLabel = typeof selectedOpt === "string" ? selectedOpt : selectedOpt.label;
                        const translatedLabel = translateValue ? translateValue(rawLabel) : rawLabel;
                        updatedState[`${field.name}_label`] = translatedLabel;
                    }
                }
            }
        });

        metadata.fields?.forEach(field => {
            if (field.calculate && (typeof field.calculate === 'function')) {
                updatedState[field.name] = field.calculate(updatedState);
            }
        })
        if (user) {
            if (!updatedState.org_name && user.org_name) updatedState.org_name = user.org_name;
            if (!updatedState.name && user.name) updatedState.name = user.name;
        }

        if (metadata.calculateFormFields) {
            const calculatedFormFieldValues = metadata.calculateFormFields(updatedState);
            if (typeof calculatedFormFieldValues !== "object") return {};
            return { ...updatedState, ...calculatedFormFieldValues };
        }
        return { ...updatedState };
    }

    const validateFormData = () => {
        let validationErrors = {};
        let validatedData = null;
        try {
            validatedData = validationSchema.validateSync(formData, { abortEarly: false });
        } catch (err) {
            err.inner?.forEach((error) => {
                validationErrors[error.path] = error.message;
            });
        }

        // Custom validation for manual brand input
        metadata.fields?.forEach((field) => {
            const value = formData[field.name];
            if (value === "-1" || value === -1 || String(value).toUpperCase() === "OTHER") {
                const options = optionsMap[field.name] || (Array.isArray(field.options) ? field.options : []);
                if (hasOtherOption(options)) {
                    const manualValue = formData[`${field.name}_manualValue`];
                    if (!manualValue || !manualValue.trim()) {
                        validationErrors[`${field.name}_manualValue`] = "This field is required";
                    }
                }
            }
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const resetErrors = setTimeout(_ => {
                setErrors(null);
                clearTimeout(resetErrors);
            }, 5000);
            return false;
        }

        return validatedData || formData;
    }

    const fetchOptions = useCallback(async (fieldName, optionsFn, optionsKey) => {
        try {
            const result = optionsFn(optionsKey);
            // Check if `result` is a promise
            const resolvedOptions = result instanceof Promise ? await result : result;
            setOptionsMap((prev) => ({
                ...prev,
                [fieldName]: resolvedOptions,
            }));
        } catch (error) {
            console.error(`Error fetching options for ${fieldName}:`, error);
        }
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateFormData()) return;
        let mutatedState = { ...formData };
        if (metadata.saveFormStateFn) {
            const saveFormState = metadata.saveFormStateFn(formData);
            mutatedState = { ...mutatedState, ...saveFormState }
        }

        // Strip fields whose showCondition / showConditionIds evaluates to false
        // so hidden/irrelevant fields are never sent in the save payload
        metadata.fields?.forEach((field) => {
            const hasCondition = field.showCondition || field.showConditionIds;
            if (hasCondition && !evaluateShowCondition(field, mutatedState)) {
                delete mutatedState[field.name];
            }
        });

        onSave(mutatedState);
    }

    if (!metadata || !formFields || formFields.length === 0) {
        return <div className="text-center">Form does not exists</div>;
    }

    return (
        <form className="row gy-3 mt-2" onSubmit={handleSubmit}>
            {formFields.map((field) => <FormField
                key={field.label || field.name}
                field={field}
                optionsMap={optionsMap}
                fetchOptions={fetchOptions}
                handleInputChange={handleInputChange}
                formState={formData}
                errors={errors}
                defaultContainerClassName={fieldContainerClassName}
            />)}
            {children}
        </form>
    );
}
